import type { definitions } from '@luxuryescapes/contract-svc-cruise'
import { capitalise } from 'lib/string/stringUtils'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import { mapUrgencyTags, formatOfferName, getCruiseProductType } from './cruiseMap'
import { cruiseInclusionsMap } from './cruiseInclusionMap'
import { sortBy, groupBy } from 'lib/array/arrayUtils'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'

type OfferSummaryResponse = definitions['OFFER_SUMMARY']

export function cruiseOfferSummaryMap(cruise: OfferSummaryResponse, regionCode: string): App.CruiseOffer {
  const offerName = formatOfferName(cruise.name)
  const [lowestPriceDeparture] = sortBy(cruise.departures, (departure) => departure.lowestPrice.total, 'asc')
  const mainDepartureDetails = mainDepartureDetailsMap(cruise, regionCode)
  const lowestDeparturePricesByCategory = lowestDeparturePricesByCategoryMap(cruise)
  const departuresSummary = departureSummaryMap(cruise.departures)
  const offerInclusions = cruise.inclusions as Array<App.Cruises.OfferInclusion>
  const hasMemberInclusions = !!mainDepartureDetails.luxPlusInclusionsByTier

  return {
    id: cruise.id,
    productType: getCruiseProductType(cruise),
    cruiseLine: cruiseLineMap(cruise.vendor),
    analyticsType: OFFER_TYPE_CRUISE,
    name: offerName,
    slug: cruise.id,
    disableDeposits: !config.DEPOSITS_ENABLED || !cruise.depositAvailable,
    type: OFFER_TYPE_CRUISE,
    parentType: OFFER_TYPE_CRUISE,
    departures: {},
    departuresSummary,
    duration: cruise.duration,
    departurePort: cruise.port.departure.name,
    departureDates: cruise.availableDepartureDates,
    returnPort: cruise.port.arrival.name,
    ship: shipMap(cruise.ship),
    images: cruise.images.map((image) => ({
      id: image.imageId,
      title: offerName,
    })),
    itinerary: itineraryMap(cruise.itineraries),
    lowestDeparturePricesByCategory,
    lowestDeparturePrice: {
      currencyCode: lowestPriceDeparture.lowestPrice.currencyCode,
      cabinCategory: lowestPriceDeparture.lowestPrice.cabinType,
      adultFare: lowestPriceDeparture.lowestPrice.total,
      total: lowestPriceDeparture.lowestPrice.total,
      value: lowestPriceDeparture.lowestPrice.total,
      rateCode: lowestPriceDeparture.lowestPrice.rateCode ?? null,
      brochurePrice: lowestPriceDeparture.lowestPrice.discountPill?.totalAmount ?? undefined,
      brochureDiscountPrice: lowestPriceDeparture.lowestPrice.discountPill?.discountPrice ?? undefined,
      brochureDiscountPercentage: lowestPriceDeparture.lowestPrice.discountPill?.percentage ?? undefined,
    },
    mainDepartureDetails,
    evergreenInclusions: cruise.evergreenInclusions?.map<App.EvergreenInclusion>((serverEvergreenInclusion) => {
      return {
        id: uuidV4(),
        description: serverEvergreenInclusion.inclusion,
        cabinCategories: serverEvergreenInclusion.cabinCategories,
        cabinTypes: serverEvergreenInclusion.cabinTypes,
        shipId: serverEvergreenInclusion.shipId,
      }
    }) ?? [],
    urgencyTags: mapUrgencyTags(),
    luxPlus: {
      hasMemberInclusions,
    },
    offerInclusions,
    loyaltyProductType: cruise.loyaltyProductType,
    campaignTags: cruise.campaignTags ?? [],
    cruiseType: cruise.cruiseType,
  }
}

function departureSummaryMap(departures: OfferSummaryResponse['departures']) {
  const departuresMap = groupBy(departures.map(departure => ({
    id: departure.id,
    departureDate: departure.departureDate,
    arrivalDate: departure.arrivalDate,
    lowestPriceTotal: departure.lowestPrice.total,
    lowestPriceCurrency: departure.lowestPrice.currencyCode,
  })), (departure) => departure.departureDate.slice(0, -3))
  return Object.fromEntries(departuresMap.entries()) as Record<string, Array<App.CruiseDepartureSummary>>
}

function cruiseLineMap(cruiseLine: OfferSummaryResponse['vendor']): App.CruiseLine {
  return {
    id: cruiseLine.id,
    name: cruiseLine.name,
    imageId: cruiseLine.imageId ?? undefined,
  }
}

function shipMap(ship: OfferSummaryResponse['ship']): App.CruiseShip {
  return {
    id: ship.id,
    name: ship.name,
    images: [],
  }
}

function itineraryMap(
  itineraryItems: OfferSummaryResponse['itineraries'],
) {
  return itineraryItems
    .map((itinerary) => itineraryItemMap(itinerary))
    .sort((a, b) => a.startDay - b.startDay)
}

function itineraryItemMap(itinerary: OfferSummaryResponse['itineraries'][number]) {
  return {
    title: (itinerary.port?.name || itinerary.description || 'At sea').split(',')[0],
    startDay: itinerary.dayNumber,
    description: itinerary.description ?? '',
    startTime: '',
    endTime: '',
    port: itinerary.port ? {
      name: itinerary.port.name || '',
      city: itinerary.port.city || '',
      countryCode: itinerary.port.countryCode ?? '',
      nearestAirportCode: '',
      address: '',
      stateProvince: '',
      latitude: itinerary.port.latitude,
      longitude: itinerary.port.longitude,
    } : null,
  }
}

function mainDepartureDetailsMap(cruise: OfferSummaryResponse, regionCode: string): App.CruiseDepartureDetails {
  const [lowestDeparture] = sortBy(cruise.departures, (departure) => departure.lowestPrice.total, 'asc')
  const lowestOverallPriceDetails = lowestOverallPriceDetailsMap(cruise)
  const lowestPriceDetailsByCategory = lowestPriceDetailsByCategoryMap(cruise)
  const offerInclusions = cruise.inclusions as Array<App.Cruises.OfferInclusion>

  const { luxPlusInclusionsByTier, standardInclusions, inclusionDetails } = cruiseInclusionsMap({
    inclusions: offerInclusions ?? [],
    departureId: lowestDeparture.id,
    lowestRateCode: lowestOverallPriceDetails.rateCode!,
    regionCode,
    isMainDeparture: true,
  })

  return {
    id: lowestDeparture.id,
    lowestOverallPriceDetails,
    lowestPriceDetailsByCategory,
    departureDate: cruise.mainDepartureDate,
    externalId: null,
    arrivalDate: '',
    minimumSupervisorAge: 21,
    luxPlusInclusionsByTier,
    standardInclusions,
    inclusionDetails,
  }
}

function lowestOverallPriceDetailsMap(cruise: OfferSummaryResponse): App.CruisePriceDetails {
  const [lowestPricedDeparture] = sortBy(cruise.departures, (dep) => dep.lowestPrice.total, 'asc')

  const discountPills = lowestPricedDeparture.lowestPrice.discountPill ? {
    price: lowestPricedDeparture.lowestPrice.discountPill.totalAmount,
    discountPercentage: lowestPricedDeparture.lowestPrice.discountPill.percentage,
    discountPrice: lowestPricedDeparture.lowestPrice.discountPill.discountPrice,
  } : undefined

  return {
    total: lowestPricedDeparture.lowestPrice.total,
    value: lowestPricedDeparture.lowestPrice.total,
    adultFare: lowestPricedDeparture.lowestPrice.total,
    cabinCategory: capitalise(lowestPricedDeparture.lowestPrice.cabinType),
    isTaxIncluded: true,
    isNccfIncluded: true,
    rateCode: lowestPricedDeparture.lowestPrice.rateCode ?? undefined,
    currencyCode: lowestPricedDeparture.lowestPrice.currencyCode,
    discountPills,
  }
}

function lowestDeparturePricesByCategoryMap(cruise: OfferSummaryResponse): App.LowestDeparturePricesByCategory {
  const lowestPricesByCategory = cruise.lowestPricesByCategory

  if (!lowestPricesByCategory) {
    return {}
  }

  // Convert API category prices to LowestPricesByCategory format
  const categoryPrices: App.LowestPricesByCategory = {}
  Object.entries(lowestPricesByCategory).forEach(([category, price]) => {
    if (price) {
      const cabinType = capitalise(category)
      categoryPrices[cabinType] = {
        currencyCode: price.currencyCode,
        cabinCategory: cabinType,
        adultFare: price.total,
        total: price.total,
        value: price.total,
        rateCode: price.rateCode,
        brochurePrice: price.discountPill?.totalAmount,
        brochureDiscountPrice: price.discountPill?.discountPrice,
        brochureDiscountPercentage: price.discountPill?.percentage,
      }
    }
  })

  // Map each departure to have all available categories
  return cruise.departures.reduce<App.LowestDeparturePricesByCategory>(
    (acc, departure) => {
      const departureId = departure.id

      // For each departure, include all available cabin categories from offer level
      acc[departureId] = categoryPrices

      return acc
    }, {})
}

function lowestPriceDetailsByCategoryMap(cruise: OfferSummaryResponse): Record<string, App.CruisePriceDetails> {
  const lowestPricesByCategory = cruise.lowestPricesByCategory as Record<string, {
    rateCode: string | null
    total: number
    currencyCode: string
  }> | undefined

  if (!lowestPricesByCategory) {
    return {}
  }

  return Object.entries(lowestPricesByCategory).reduce<Record<string, App.CruisePriceDetails>>((acc, [category, price]) => {
    if (price) {
      const cabinType = capitalise(category)
      acc[cabinType] = {
        total: price.total,
        value: price.total,
        adultFare: price.total,
        isTaxIncluded: true,
        isNccfIncluded: true,
        currencyCode: price.currencyCode,
        rateCode: price.rateCode ?? undefined,
        cabinCategory: cabinType,
      }
    }
    return acc
  }, {})
}
